import { useState, useRef, useEffect, useContext } from 'react';
import { classnames } from 'helpers/utils';
import { VaultsController } from 'networking/controllers/vaults-controller';
import { AppContext, appActions } from 'context';
import { NotificationObject } from 'models/notificationObject';
import { NotificationType } from 'common/enums';

interface ContentEntryTitleProps {
  title?: string;
  onTitleChange: (newTitle: string) => void;
  contentId?: number;
  placeholder?: string;
  isCreatingNew?: boolean;
  editable?: boolean;
}

export const ContentEntryTitle = ({
  title,
  onTitleChange,
  contentId,
  placeholder = 'Entry title goes right here',
  isCreatingNew = false,
  editable = true,
}: ContentEntryTitleProps) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentTitle, setCurrentTitle] = useState(title);
  const { dispatch } = useContext(AppContext);

  const handleTitleChange = async (newTitle: string) => {
    setIsEditMode(false);

    if (isCreatingNew || !contentId) {
      onTitleChange(newTitle);
      return;
    }

    try {
      await VaultsController.updateContentMetadata(contentId, {
        name: newTitle,
      });
      onTitleChange(newTitle);
      dispatch({
        type: appActions.NOTIFICATION,
        notification: new NotificationObject({
          show: true,
          title: 'Entry title updated',
          type: NotificationType.Success,
        }),
      });
    } catch (error) {
      dispatch({
        type: appActions.NOTIFICATION,
        notification: new NotificationObject({
          show: true,
          title: 'The title could not be updated',
          message: 'Please try again later.',
          type: NotificationType.Error,
        }),
      });
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCurrentTitle(e.target.value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleTitleChange(currentTitle || placeholder);
    } else if (e.key === 'Escape') {
      setCurrentTitle(title);
      setIsEditMode(false);
    }
  };

  const handleBlur = () => {
    if (!currentTitle?.trim()) {
      setCurrentTitle(title);
      setIsEditMode(false);
      return;
    }

    handleTitleChange(currentTitle);
  };

  const handleContainerClick = () => {
    if (!editable) return;
    setIsEditMode(true);
  };

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditMode && inputRef.current) {
      const input = inputRef.current;
      input.focus();
      const { length } = input.value;
      input.setSelectionRange(length, length);
    }
  }, [isEditMode]);

  useEffect(() => {
    setCurrentTitle(title);
  }, [title]);

  return (
    <div
      className={classnames(
        'flex w-full min-w-0 flex-1 items-start justify-between transition-colors',
        {
          // Create-entry form: a large, clean title field with a bottom rule
          // (no rounded box).
          'rounded-none border-0 border-b border-solid border-[#d8d8d0] px-1 py-3 focus-within:border-[#7c7c67]':
            isCreatingNew,
          'cursor-text': isCreatingNew,
          'rounded-[14px] px-3 py-2': !isCreatingNew,
          'bg-gray-100': isEditMode && !isCreatingNew,
          'cursor-pointer hover:bg-gray-100':
            !isEditMode && editable && !isCreatingNew,
          'cursor-default': !isEditMode && !editable,
        },
      )}
      onClick={editable ? handleContainerClick : undefined}
      onKeyDown={(e) => {
        if (!editable) return;
        if (e.key === 'Enter' || e.key === ' ') {
          handleContainerClick();
        }
      }}
      role="button"
      tabIndex={0}
    >
      {isEditMode ? (
        <input
          type="text"
          name="content-title"
          value={currentTitle}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          placeholder={placeholder}
          className={classnames(
            'text-body-semi-bold-medium-gray800 w-full bg-transparent placeholder:text-gray-300',
            { 'text-[22px] leading-8': isCreatingNew },
          )}
          ref={inputRef}
        />
      ) : (
        <span
          className={classnames('min-w-0 flex-1 break-words', {
            'text-body-semi-bold-medium-gray800': !!title,
            'text-body-semi-bold-medium-gray400': !title,
            'text-[22px] leading-8': isCreatingNew,
          })}
        >
          {title || placeholder}
        </span>
      )}
    </div>
  );
};
